import React, { useState, useEffect } from 'react';
import PestDetection from './pages/PestDetection';
import Weather from './pages/Weather';
import SellProduct from './pages/SellProduct';
import BuyProduct from './pages/BuyProduct';
import Community from './pages/Community';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Schemes from './pages/Schemes';
import Header from './components/Header';
import Footer from './components/Footer';
import AdminDashboard from './pages/AdminDashboard';
import AdminLogin from './pages/AdminLogin';

function App() {
  const [tab, setTab] = useState('home');

  const [currentUser, setCurrentUser] = useState(null);

  // Simple scroll to top on tab change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [tab]);

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden">
      {/* Decorative background blobs */}
      <div className="fixed top-0 left-0 w-96 h-96 bg-emerald-200/20 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2 -z-10 pointer-events-none"></div>
      <div className="fixed bottom-0 right-0 w-96 h-96 bg-blue-200/20 rounded-full blur-3xl translate-x-1/2 translate-y-1/2 -z-10 pointer-events-none"></div>

      <Header onNavigate={setTab} active={tab} user={currentUser} onLogout={() => setCurrentUser(null)} />

      {/* Spacer for fixed header */}
      <main className="flex-1 pt-24 w-full mx-auto">
        {tab === 'home' && (
          <div className="animate-fade-in">
            <PestDetection />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
              {/* Stats Section - Refined */}
              <section className="mb-16">
                <div className="text-center mb-10">
                  <span className="text-emerald-600 font-bold tracking-wider uppercase text-sm">Why Choose Us</span>
                  <h2 className="text-3xl md:text-4xl font-extrabold text-slate-800 mt-2">Data Science for Growers</h2>
                  <p className="max-w-2xl mx-auto mt-4 text-slate-600">Leverage AI to maximize yields and minimize losses with precision agriculture.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div className="group bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all border border-slate-100 hover:border-emerald-100 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-50 rounded-bl-full -mr-4 -mt-4 transition-all group-hover:bg-emerald-100"></div>
                    <div className="relative z-10">
                      <div className="text-5xl font-black text-emerald-600 mb-2">25%</div>
                      <div className="text-lg font-bold text-slate-800">Savings on</div>
                      <div className="text-slate-500">Crop Inputs</div>
                    </div>
                  </div>

                  <div className="group bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all border border-slate-100 hover:border-blue-100 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-blue-50 rounded-bl-full -mr-4 -mt-4 transition-all group-hover:bg-blue-100"></div>
                    <div className="relative z-10">
                      <div className="text-5xl font-black text-blue-600 mb-2">30%</div>
                      <div className="text-lg font-bold text-slate-800">Savings on</div>
                      <div className="text-slate-500">Crop Loss</div>
                    </div>
                  </div>

                  <div className="group bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all border border-slate-100 hover:border-purple-100 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-purple-50 rounded-bl-full -mr-4 -mt-4 transition-all group-hover:bg-purple-100"></div>
                    <div className="relative z-10">
                      <div className="text-5xl font-black text-purple-600 mb-2">50%</div>
                      <div className="text-lg font-bold text-slate-800">Savings on</div>
                      <div className="text-slate-500">Scouting Time</div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Feature Highlights - Refined */}
              <section className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl shadow-2xl p-10 md:p-16 text-white overflow-hidden relative">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)', backgroundSize: '24px 24px' }}></div>

                <div className="relative z-10 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 text-center">
                  <div className="space-y-2">
                    <div className="text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-b from-emerald-400 to-emerald-600">98%</div>
                    <div className="text-slate-300 font-medium tracking-wide">Detection Accuracy</div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-b from-blue-400 to-blue-600">200+</div>
                    <div className="text-slate-300 font-medium tracking-wide">Diseases & Pests</div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-b from-amber-400 to-amber-600">&lt;3s</div>
                    <div className="text-slate-300 font-medium tracking-wide">Analysis Time</div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-b from-purple-400 to-purple-600">24/7</div>
                    <div className="text-slate-300 font-medium tracking-wide">Available Support</div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        )}
        {tab === 'weather' && <Weather />}
        {tab === 'community' && <Community />}
        {tab === 'market_sell' && <SellProduct />}
        {tab === 'market_buy' && <BuyProduct />}
        {tab === 'schemes' && <Schemes />}
        {tab === 'login' && <Login onNavigate={setTab} onLoginSuccess={setCurrentUser} />}

        {tab === 'signup' && <Signup onNavigate={setTab} onSignupSuccess={setCurrentUser} />}
        {tab === 'admin_login' && <AdminLogin onNavigate={setTab} />}
        {tab === 'admin' && <AdminDashboard onNavigate={setTab} onLogout={() => setTab('home')} />}
      </main>

      {tab === 'home' && <Footer />}
    </div>
  );
}

export default App;
